import React, { useMemo } from "react";

/**
 * LivingCircuit — animated SVG network of nodes + connecting lines.
 * Pure CSS animations for GPU acceleration.
 * Position absolute (scrolls with the page).
 */
const SEED = (() => {
  const W = 1600, H = 2400;
  const cols = 11, rows = 14;
  const nodes = [];
  let id = 0;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const jx = ((id * 9301 + 49297) % 233280) / 233280 - 0.5;
      const jy = ((id * 1103515245 + 12345) % 233280) / 233280 - 0.5;
      nodes.push({
        id: id++,
        x: (W / (cols - 1)) * c + jx * 80,
        y: (H / (rows - 1)) * r + jy * 60,
      });
    }
  }
  const edges = [];
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const dx = nodes[i].x - nodes[j].x;
      const dy = nodes[i].y - nodes[j].y;
      const d = Math.sqrt(dx * dx + dy * dy);
      if (d < 230) edges.push({ a: i, b: j, d });
    }
  }
  return { nodes, edges, W, H };
})();

export const LivingCircuit = () => {
  const { nodes, edges, W, H } = SEED;

  const flowEdges = useMemo(() => {
    return edges.filter((_, i) => i % 3 === 0);
  }, [edges]);

  return (
    <div className="living-circuit-wrap absolute inset-0 pointer-events-none z-0 overflow-hidden" aria-hidden data-testid="living-circuit">
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMin slice"
      >
        <defs>
          <radialGradient id="lc-glow-purple" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#7B2FBE" stopOpacity="1" />
            <stop offset="100%" stopColor="#7B2FBE" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="lc-glow-teal" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#2DD4BF" stopOpacity="1" />
            <stop offset="100%" stopColor="#2DD4BF" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Static thin connection lines */}
        <g className="lc-static-lines">
          {edges.map((e, i) => {
            const a = nodes[e.a], b = nodes[e.b];
            const teal = i % 11 === 0;
            return (
              <line
                key={`s-${i}`}
                x1={a.x}
                y1={a.y}
                x2={b.x}
                y2={b.y}
                stroke={teal ? "#2DD4BF" : "#7B2FBE"}
                strokeOpacity="0.30"
                strokeWidth="0.9"
              />
            );
          })}
        </g>

        {/* Animated data-flow lines */}
        <g className="lc-flow-lines">
          {flowEdges.map((e, i) => {
            const a = nodes[e.a], b = nodes[e.b];
            const len = Math.round(e.d);
            const isTeal = i % 3 === 0;
            return (
              <line
                key={`f-${i}`}
                x1={a.x}
                y1={a.y}
                x2={b.x}
                y2={b.y}
                stroke={isTeal ? "#2DD4BF" : "#7B2FBE"}
                strokeOpacity="0.85"
                strokeWidth="1.3"
                strokeDasharray={`${len * 0.16} ${len * 0.84}`}
                style={{
                  animation: `lc-dash ${5 + (i % 6)}s linear infinite`,
                  animationDelay: `${(i * 0.35) % 6}s`,
                }}
              />
            );
          })}
        </g>

        {/* Nodes */}
        <g className="lc-nodes">
          {nodes.map((n, i) => {
            const big = i % 5 === 0;
            const teal = i % 7 === 0;
            return (
              <g key={`n-${n.id}`}>
                <circle
                  cx={n.x}
                  cy={n.y}
                  r={big ? 3.0 : 1.8}
                  fill={teal ? "#2DD4BF" : "#7B2FBE"}
                  fillOpacity="0.9"
                />
                <circle
                  cx={n.x}
                  cy={n.y}
                  r="14"
                  fill={`url(#${teal ? "lc-glow-teal" : "lc-glow-purple"})`}
                  fillOpacity="0"
                  style={{
                    animation: `lc-pulse ${3.5 + (i % 5)}s ease-in-out infinite`,
                    animationDelay: `${(i * 0.21) % 6}s`,
                    transformOrigin: `${n.x}px ${n.y}px`,
                  }}
                />
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
};

export default LivingCircuit;
